import { api, aufSitzungsverlust, tokenLesen, tokenSetzen, type Angemeldet } from './api.ts';
import { anmeldungVorbereiten, vergessen } from './zugang.ts';

/**
 * Angemeldetes Portalkonto als globaler Zustand.
 *
 * Anders als in der internen Oberfläche liegt hier auch der Kunde selbst:
 * Das Portal zeigt auf jeder Seite, um wessen Daten es geht — das beruhigt und
 * verhindert das Gefühl, im falschen Konto zu sein.
 */

export interface Kunde {
  id: string;
  kd_nummer: string;
  typ: string;
  anzeigename: string;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  status: string;
  /**
   * Als Mitarbeiter kostenfrei freigeschaltet (Migration 032): PREMIUM und
   * beide Module laufen für 0,00 €. Das Portal zeigt dann keine Preise und
   * keine Kaufknöpfe — die Server-Endpunkte weisen jede Buchung ohnehin ab.
   */
  mitarbeiter: boolean;
}

export interface Paket {
  id: string;
  typ: string;
  status: string;
  zahlweise: string | null;
  preis: number | null;
  name: string | null;
  kennung: string | null;
  aktiv_seit: string | null;
  endet_am: string | null;
  lesefrist_bis: string | null;
  sperrgrund: string | null;
  einheiten_monat: number | null;
  saldo: number | null;
  naechster_verfall: string | null;
  naechster_verfall_menge: number | null;
  /** Ende der laufenden Abrechnungsperiode — Stripe-Pakete only. */
  periode_bis: string | null;
  /** Ende eines laufenden, vorausbezahlten Jahrespakets. NULL = normale Monatsabrechnung. */
  bezahlt_bis: string | null;
  /** Ende der kostenlosen Testphase bei Erstbuchung eines Moduls. NULL = keine Testphase. */
  testphase_bis: string | null;
  /** Geplanter Downgrade, wirkt zum Ende der laufenden Periode. */
  naechster_tarif_name: string | null;
  naechster_tarif_kennung: string | null;
  naechster_tarif_ab: string | null;
  /** Letztes Upgrade oder Tarifeinheiten-Erneuern — dafür gilt 24 Stunden Kühlzeit. */
  letzte_tarifaenderung_am: string | null;
  /** Verknüpfte Module eines individuellen Vertrages (Produkt Safe-Paket, Migration 038). */
  modul: string | null;
}

/**
 * Hat die Person gerade Zugang zu einem Modul — sei es über ein eigenes
 * Einzelpaket (`typ` = 'vault'/'das', vor dem 26.08.2026 der einzige Weg)
 * oder über einen individuellen Vertrag (Produkt Safe-Paket, Migration 038),
 * der das Modul über die `modul`-Spalte mit abdeckt. Beide Wege prüfen,
 * damit z. B. der Akten-Safe-Reiter für Safe-Paket-Kunden nicht unsichtbar
 * bleibt, nur weil sie kein eigenes 'das'-Paket mehr haben.
 */
export function hatModulZugang(pakete: Paket[], typ: 'vault' | 'das'): boolean {
  return pakete.some(
    (p) =>
      ['aktiv', 'gekuendigt'].includes(p.status) &&
      (p.typ === typ || (p.typ === 'dienstleistung' && (p.modul === typ || p.modul === 'beide'))),
  );
}

export interface Tarif {
  id: string;
  paket_typ: string;
  kennung: string;
  name: string;
  beschreibung: string | null;
  preis_monat_brutto: number | null;
  preis_jahr_brutto: number | null;
  preis_jahr_mit_paket_brutto: number | null;
  preis_monat_mit_paket_brutto: number | null;
  einheiten_monat: number | null;
  speicher_limit_mb: number | null;
}

/**
 * Eine Organisation, der die angemeldete Person angehört.
 *
 * Ein Mensch kann in mehreren sein — Geschäftsführer der Firma und zugleich
 * 1. Vorstand des Vereins. Rechnungen, Termine und Anfragen gehören dann
 * getrennten Sätzen an, und die Umschaltung sagt, welcher gerade gemeint ist.
 */
export interface Organisation {
  kundeId: string;
  kdNummer: string;
  anzeigename: string;
  typ: string;
  rolle: 'inhaber' | 'mitglied';
}

class Sitzung {
  konto = $state<Angemeldet | null>(null);
  kunde = $state<Kunde | null>(null);
  pakete = $state<Paket[]>([]);
  organisationen = $state<Organisation[]>([]);
  gewaehlt = $state<string | null>(null);
  /** Läuft gerade ein Wechsel? Solange dürfen die Seiten nicht nachladen. */
  wechselt = $state(false);
  /** Dürfen Kunden im Portal selbst buchen? Seit 26.08.2026 aus (Schalter am Server). */
  selbstbuchung = $state(false);
  geprueft = $state(false);

  constructor() {
    aufSitzungsverlust(() => {
      this.konto = null;
      this.kunde = null;
      this.pakete = [];
      this.geprueft = true;
      // Ohne Sitzung kein Akten-Safe — der Hauptschlüssel hat dann auch
      // nichts mehr im Speicher zu suchen.
      vergessen();
    });
  }

  async laden(): Promise<void> {
    if (!tokenLesen()) {
      this.geprueft = true;
      return;
    }
    try {
      const antwort = await api.get<{
        konto: Angemeldet;
        kunde: Kunde;
        pakete: Paket[];
        organisationen?: Organisation[];
        gewaehlt?: string;
        selbstbuchung?: boolean;
      }>('/api/portal/ich');
      this.konto = antwort.konto;
      this.kunde = antwort.kunde;
      this.pakete = antwort.pakete;
      // Bei jedem Laden frisch: Eine Mitgliedschaft kann dazugekommen oder
      // beendet worden sein, seit sich der Kunde angemeldet hat.
      this.organisationen = antwort.organisationen ?? [];
      this.gewaehlt = antwort.gewaehlt ?? null;
      this.selbstbuchung = antwort.selbstbuchung === true;
    } catch {
      tokenSetzen(null);
      this.konto = null;
    } finally {
      this.geprueft = true;
    }
  }

  /**
   * Das Passwort bleibt hier. An den Server geht nur der daraus abgeleitete
   * Beweis — aus ihm lässt sich weder das Passwort zurückrechnen noch der
   * Schlüssel, der später den Akten-Safe aufschließt.
   */
  async anmelden(email: string, passwort: string): Promise<void> {
    const adresse = email.trim().toLowerCase();
    // Behält den Hauptschlüssel im Arbeitsspeicher: Aus ihm entstehen später
    // die Schlüssel, die der eingebettete Akten-Safe braucht.
    const beweis = await anmeldungVorbereiten(adresse, passwort);
    const antwort = await api.post<{ token: string }>('/api/portal/anmeldung', {
      email: adresse,
      beweis,
    });
    tokenSetzen(antwort.token);
    await this.laden();
  }

  /**
   * Auf eine andere Organisation umschalten.
   *
   * Der Server prüft die Mitgliedschaft — hier wird nur gefragt. Danach lädt
   * alles neu, denn Kunde, Pakete und alle Listen gehören jetzt zu einer
   * anderen Organisation. Die Seite springt bewusst auf die Übersicht: Eine
   * offene Rechnung des vorigen Kunden weiter anzuzeigen, wäre schlimmer als
   * ein Sprung.
   */
  async organisationWechseln(kundeId: string): Promise<void> {
    if (kundeId === this.gewaehlt || this.wechselt) return;
    this.wechselt = true;
    try {
      await api.post('/api/portal/organisation', { kundeId });
      await this.laden();
      location.hash = '/';
    } finally {
      this.wechselt = false;
    }
  }

  async abmelden(): Promise<void> {
    try {
      await api.post('/api/portal/abmeldung');
    } finally {
      tokenSetzen(null);
      vergessen();
      this.konto = null;
      this.kunde = null;
      this.pakete = [];
      location.hash = '/';
    }
  }
}

export const sitzung = new Sitzung();

/** Beträge und Daten – im Portal immer ausgeschrieben, nie abgekürzt. */
export function euro(betrag: number | null | undefined): string {
  if (betrag === null || betrag === undefined) return '—';
  return Number(betrag).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export function datum(wert: string | null | undefined): string {
  if (!wert) return '—';
  return new Date(wert).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function zeitpunkt(wert: string | null | undefined): string {
  if (!wert) return '—';
  return new Date(wert).toLocaleString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const PAKETNAME: Record<string, string> = {
  einheiten: 'Ihr Einheitenpaket',
  vault: 'Passwort-Safe – Ihr Passworttresor',
  das: 'Akten-Safe – Ihre Dokumente',
  dienstleistung: 'Ihre Vereinbarung',
  geraete: 'Ihre Geräte',
};
